import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { Toast } from "./Toast";

const SEEN_KEY = "amendie_seen_invite_claims";

interface Claim {
  id: string;
  email: string;
  tenantName: string;
  claimedAt: string;
}
interface InvitesResp {
  pending: unknown[];
  accepted: Claim[];
}

function readSeen(): Set<string> | null {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw === null ? null : new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}
function writeSeen(ids: string[]): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(ids));
  } catch {
    /* storage disabled — the toast just won't persist "seen" */
  }
}

/**
 * Operator-only: when someone accepts a bypass invite (a pre-created account is claimed on first
 * sign-in), pop a toast for the operator. MVP of the notify feature — poll the accepted list
 * (`GET /settings/invites` → `accepted`, backed by listRecentlyClaimed) and diff it against what THIS
 * browser has already seen. The seen-set is seeded silently on first run so pre-existing claims don't
 * spam a toast; only claims that land afterwards nudge. A full bell/notification center is a later step.
 * Render only when the caller is an operator (the endpoint 403s otherwise).
 */
export function InviteAcceptedToast() {
  const [claim, setClaim] = useState<Claim | null>(null);
  const busy = useRef(false);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        const { accepted } = await api<InvitesResp>("/settings/invites");
        if (!alive) return;
        const ids = accepted.map((a) => a.id);
        const seen = readSeen();
        if (seen === null) {
          writeSeen(ids); // first run on this browser: seed, don't toast old claims
          return;
        }
        const fresh = accepted.filter((a) => !seen.has(a.id));
        if (fresh.length > 0) {
          setClaim(fresh[0]); // surface the newest unseen claim
          writeSeen(ids); // mark all current as seen so it shows once
        }
      } catch {
        /* not an operator / transient failure — ignore */
      } finally {
        busy.current = false;
      }
    };
    void check();
    const iv = setInterval(() => void check(), 120_000);
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  if (!claim) return null;
  return (
    <Toast
      tone="gold"
      title="Invite accepted"
      body={`${claim.email} just signed in${claim.tenantName ? ` · ${claim.tenantName}` : ""}.`}
      onClose={() => setClaim(null)}
    />
  );
}
